import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { createReduxEnhancer } from '@sentry/react';

import { mechanicApi } from 'widgets/mechanic/api/mechanicApi';
import { mechanicDetailApi } from 'widgets/mechanicDetailPage/api/mechanicDetailApi';
import { authApi } from 'entities/auth';
import { usersApi } from 'entities/users';
import { contentApi } from 'entities/content/model/contentApi';
import authSlice from 'entities/auth/model/authSlice';
import uiSlice from 'features/ui/model/uiSlice';
import { pagesApi } from 'entities/pages';
import { activitiesApi } from 'entities/activities';
import { mechanicApiPost } from 'widgets/mechanic/api/mechanicApiPost';
import { mechanicDetailApiPost } from 'widgets/mechanicDetailPage/api/mechanicDetailApiPost';
import { partApi } from 'features/getParticipants/api/model/usersApi';
import { notificationsApi } from 'entities/notifications';
import { transactionsApi } from 'entities/transactions';
import { newsApi } from 'entities/news/model/newsApi';
import { categoryApi } from 'widgets/category/api/categoryApi';
import { partnerApi } from 'widgets/partner/api/partnerApi';
import { shopApiPost } from 'widgets/product/ui/api/shopApiPost';
import { bannerTypeApi } from 'widgets/content/ui/banner-type/api/bannerTypeApi';
import { prizesApi } from 'entities/prize';
import { competitionsB2BApi } from 'entities/competitions-b2b/api';
import { prizeSlice } from 'features/prize/createNew/model';
import { citiesSlice } from 'features/getCities/model/citiesSlice';
import { reportPromoApi } from 'entities/reportPromo/api/reportPromoApi';
import { downloadParticipantsApi } from 'entities/downloads/participants/model/downloadsParticipantsApi';
import { downloadBonusesApi } from 'entities/downloads/bonuses/model/downloadsBonusesApi';
import { downloadPrizesApi } from 'entities/downloads/prizes/model/downloadsPrizesApi';
import { financeOperatorsApi } from 'entities/financeOperations/model';
import { sendOTPApi } from 'entities/sendOTP/model';
import { bannerApi } from 'entities/banner/model';
import { competitionsB2CRApi } from 'entities/competitions-b2cr';

export const store = configureStore({
  reducer: {
    [notificationsApi.reducerPath]: notificationsApi.reducer,
    [transactionsApi.reducerPath]: transactionsApi.reducer,
    [authApi.reducerPath]: authApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [downloadParticipantsApi.reducerPath]: downloadParticipantsApi.reducer,
    [downloadBonusesApi.reducerPath]: downloadBonusesApi.reducer,
    [downloadPrizesApi.reducerPath]: downloadPrizesApi.reducer,
    [pagesApi.reducerPath]: pagesApi.reducer,
    [activitiesApi.reducerPath]: activitiesApi.reducer,
    [contentApi.reducerPath]: contentApi.reducer,
    [mechanicApi.reducerPath]: mechanicApi.reducer,
    [mechanicDetailApi.reducerPath]: mechanicDetailApi.reducer,
    [mechanicApiPost.reducerPath]: mechanicApiPost.reducer,
    [mechanicDetailApiPost.reducerPath]: mechanicDetailApiPost.reducer,
    [partApi.reducerPath]: partApi.reducer,
    [newsApi.reducerPath]: newsApi.reducer,
    [shopApiPost.reducerPath]: shopApiPost.reducer,
    [categoryApi.reducerPath]: categoryApi.reducer,
    [partnerApi.reducerPath]: partnerApi.reducer,
    [bannerTypeApi.reducerPath]: bannerTypeApi.reducer,
    [bannerApi.reducerPath]: bannerApi.reducer,
    [prizesApi.reducerPath]: prizesApi.reducer,
    [reportPromoApi.reducerPath]: reportPromoApi.reducer,
    [competitionsB2BApi.reducerPath]: competitionsB2BApi.reducer,
    [competitionsB2CRApi.reducerPath]: competitionsB2CRApi.reducer,
    [financeOperatorsApi.reducerPath]: financeOperatorsApi.reducer,
    [sendOTPApi.reducerPath]: sendOTPApi.reducer,
    auth: authSlice,
    ui: uiSlice,
    cities: citiesSlice.reducer,
    prizeSlice: prizeSlice.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({}).concat([
      authApi.middleware,
      usersApi.middleware,
      downloadParticipantsApi.middleware,
      downloadBonusesApi.middleware,
      downloadPrizesApi.middleware,
      pagesApi.middleware,
      activitiesApi.middleware,
      contentApi.middleware,
      mechanicApi.middleware,
      mechanicDetailApi.middleware,
      mechanicApiPost.middleware,
      mechanicDetailApiPost.middleware,
      partApi.middleware,
      notificationsApi.middleware,
      transactionsApi.middleware,
      newsApi.middleware,
      shopApiPost.middleware,
      categoryApi.middleware,
      partnerApi.middleware,
      bannerTypeApi.middleware,
      bannerApi.middleware,
      prizesApi.middleware,
      reportPromoApi.middleware,
      competitionsB2BApi.middleware,
      competitionsB2CRApi.middleware,
      financeOperatorsApi.middleware,
      sendOTPApi.middleware,
    ]),
  enhancers: [createReduxEnhancer()],
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type RootThunkAPI = {
  getState: () => RootState;
  dispatch: AppDispatch;
};
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
